import { Head, Link, router, usePage } from "@inertiajs/react"
import { useMemo, useState } from "react"

import { MetaBite } from "@/components/meta-bite"
import type { MetaBiteProps } from "@/components/meta-bite"
import { PlaceholderPattern } from "@/components/placeholder-pattern"
import { Prompt } from "@/components/prompt"
import { PromptTabs } from "@/components/prompt-tabs"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { useCable } from "@/lib/use-cable"
import { draftPath, followUpsDraftPath } from "@/routes"

const nextStep = (currentStep: Step): Step => {
  switch (currentStep) {
    case "idea":
      return "meta"
    case "meta":
      return "structure"
    case "structure":
      return "text"
    default:
      throw new Error("Invalid step")
  }
}

const prevStep = (currentStep: Step): Step => {
  switch (currentStep) {
    case "meta":
      return "idea"
    case "structure":
      return "meta"
    case "text":
      return "structure"
    default:
      throw new Error("Invalid step")
  }
}

const cb = (message: { draft: Draft }) => {
  router.replace({
    props: (current) => {
      return {
        ...current,
        ...message,
      }
    },
    preserveScroll: true,
    preserveState: true,
  })
}

interface FollowUpEntry {
  sections: Record<string, MetaBiteProps>
  question: string
  placeholder: string
  ready_to_move_on: boolean
}

type Step = "idea" | "meta" | "structure" | "text"

interface Draft {
  id: number
  name: string
  step: Step
  idea_follow_up?: FollowUpEntry
  meta_follow_up?: FollowUpEntry
  structure_follow_up?: FollowUpEntry // TODO: not really
  text_follow_up?: FollowUpEntry // TODO: not really
}

const currentSections = (draft: Draft, current_step?: Step): FollowUpEntry => {
  return (
    draft[`${current_step ?? draft.step}_follow_up`] ?? ({} as FollowUpEntry)
  )
}

export default function ShowChat() {
  const { draft, current_step } = usePage<{
    draft: Draft
    current_step?: Step
  }>().props

  const [loading, setLoading] = useState(false)

  const { sections, question, placeholder } = currentSections(
    draft,
    current_step,
  )

  const props = useMemo(() => ({ id: draft.id }), [draft.id])
  useCable("DraftChannel", { props }, (message: { draft: Draft }) => {
    cb(message)
    setLoading(false)
  })

  const renderItems = () => {
    if (!sections) return null

    return Object.entries(sections)
      .filter(([, value]) => value && typeof value === "object")
      .map(([key, byte]) => {
        return (
          <MetaBite
            key={key}
            name={key}
            title={byte.title}
            text={byte.text}
            clarity={byte.clarity}
          />
        )
      })
  }

  return (
    <AppLayout>
      <Head title={draft.name} />

      <PromptTabs active={current_step ?? draft.step} />

      <div className="bg-card relative flex h-full min-h-[100vh] flex-1 flex-col gap-5 overflow-hidden rounded-xl px-20 py-12 pb-40 md:min-h-min">
        {sections ? (
          renderItems()
        ) : (
          <PlaceholderPattern className="flex size-full flex-1 grow rounded-xl stroke-neutral-900/20 dark:stroke-neutral-100/20" />
        )}
      </div>

      {(!current_step || draft.step === current_step) && (
        <div className="fixed right-0 bottom-3 left-0 m-auto flex max-h-[70vh] w-full max-w-100 flex-col justify-center gap-2">
          {draft.step !== "text" && (
            <Button className="w-full" asChild>
              <Link
                method="put"
                href={draftPath(draft)}
                data={{ step: nextStep(draft.step) }}
              >
                Go to the next step
              </Link>
            </Button>
          )}

          {draft.step !== "idea" && (
            <Button className="w-full" asChild>
              <Link
                method="put"
                href={draftPath(draft)}
                data={{ step: prevStep(draft.step) }}
              >
                Go to the prev step
              </Link>
            </Button>
          )}
          {!loading && question && placeholder ? (
            <>
              <Prompt
                prompt={question}
                placeholder={placeholder}
                action={followUpsDraftPath(draft)}
                onStart={() => setLoading(true)}
              />
            </>
          ) : (
            <div className="flex items-center justify-center">
              <div className="size-8 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  )
}
